import type { EchoMissionDetail } from '@vtak/shared';
import { useMissionsStore } from '@/stores/missions-store';
import { useMissionAnalysisLive } from '@/hooks/use-mission-analysis-live';
import { EchoAudioPlayer } from './echo-audio-player';
import { EchoChannels } from './echo-channels';
import { EchoAnalysis } from './echo-analysis';
import { MissionQr } from './mission-qr';
import { MissionGuests } from './mission-guests';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { ArrowLeft } from 'lucide-react';

/** Status badge color mapping */
const STATUS_STYLES: Record<string, string> = {
  STARTED: 'bg-green-500/20 text-green-400',
  ENDED: 'bg-zinc-500/20 text-zinc-400',
};

/** Detail view for a selected Echo mission — channels, analysis, audio */
export function EchoDetail({ detail }: { detail: EchoMissionDetail }): React.JSX.Element {
  useMissionAnalysisLive(detail.joinCode ?? null);

  function handleBack(): void {
    useMissionsStore.getState().setSelectedDetail(null);
    useMissionsStore.getState().clearLivekit();
  }

  const statusStyle = STATUS_STYLES[detail.status] ?? 'bg-zinc-500/20 text-zinc-400';

  return (
    <div className="flex flex-1 flex-col gap-3 overflow-hidden p-4">
      <div className="flex items-center gap-2">
        <Button size="sm" variant="ghost" onClick={handleBack} className="h-7 w-7 p-0">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h2 className="flex-1 text-sm font-bold">{detail.name}</h2>
        <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${statusStyle}`}>
          {detail.status}
        </span>
      </div>

      <EchoAudioPlayer />

      <ScrollArea className="flex-1">
        <div className="flex flex-col gap-4">
          <EchoChannels detail={detail} />
          <Separator />
          {detail.joinCode && <MissionQr joinCode={detail.joinCode} />}
          {detail.joinCode && <MissionGuests joinCode={detail.joinCode} />}
          <EchoAnalysis detail={detail} />
        </div>
      </ScrollArea>
    </div>
  );
}
